import { Platform } from 'api/models'

export const SetupPlatform = Object.freeze({
  meta: 'meta',
  google: 'google',
  both: 'both',
})

// Default is Meta/Facebook-only, matching setup_process's `platform` column server_default.
export const DEFAULT_SETUP_PLATFORM = SetupPlatform.meta

export function platformIncludesMeta(platform) {
  return platform === SetupPlatform.meta || platform === SetupPlatform.both
}

export function platformIncludesGoogle(platform) {
  return platform === SetupPlatform.google || platform === SetupPlatform.both
}

// Maps the UI-level SetupPlatform choice to the comma-separated `platforms`
// query param /suggested_tcs and /budget_min_max accept (values: meta,
// google — see app/api/v4/events/events.py, parse_setup_platforms).
export function toApiPlatformsParam(platform) {
  const parts = []
  if (platformIncludesMeta(platform)) parts.push('meta')
  if (platformIncludesGoogle(platform)) parts.push('google')
  return parts.join(',')
}

export function toApiPlatformField(platform) {
  const parts = []
  if (platformIncludesMeta(platform)) parts.push('facebook')
  if (platformIncludesGoogle(platform)) parts.push('google')
  return parts
}

export function fromApiPlatformField(platformField) {
  if (!Array.isArray(platformField) || platformField.length === 0) return DEFAULT_SETUP_PLATFORM

  const hasMeta = platformField.includes('facebook')
  const hasGoogle = platformField.includes('google')

  if (hasMeta && hasGoogle) return SetupPlatform.both
  if (hasGoogle) return SetupPlatform.google
  if (hasMeta) return SetupPlatform.meta
  return DEFAULT_SETUP_PLATFORM
}

// setup_process's `platform` column is the sole source of truth — inferred
// from targeting key presence previously broke when a platform was added
// with no content yet (key presence also drives the PUT strip logic).
export function derivePlatformSelectionFromSetup(setup) {
  return fromApiPlatformField(setup?.platform)
}

export function mapSetupPlatformToMultiValueQueryParam(platform) {
  switch (platform) {
    case SetupPlatform.meta:
      return [Platform.facebook, Platform.instagram]
    case SetupPlatform.google:
      return [Platform.google]
    case SetupPlatform.both:
      return [Platform.facebook, Platform.instagram, Platform.google]
    default:
      return []
  }
}

// Shared by both the platform picker and the Campaign Type picker (step 1,
// CampaignSetup.jsx) — same rule either way: only worth confirming when
// there's already-configured content (an in-progress setup with TCs
// selected) that the switch could actually affect, e.g. by pruning media
// that no longer fits the new platform/campaign-type's slot requirements.
export function shouldConfirmSetupChange(
  isCampaignSetupInProgress,
  selectedTcs,
  currentValue,
  nextValue,
) {
  if (currentValue === nextValue) return false
  if (!isCampaignSetupInProgress) return false
  return Array.isArray(selectedTcs) && selectedTcs.length > 0
}
